import { useT } from '@transifex/react';
import type { JSX } from 'react';

import { getConfig } from 'pkg/config/app';
import * as styles from 'pkg/config/styles';

import { useIsBrowserSupported } from 'pkg/client_browser_info';
import { isApp } from 'pkg/platform';

import Logo from 'components/Logo';
import MaterialSymbol from 'components/material-symbols';

import InfoBox from 'components/form/info-box';
import type { MaterialSymbolVariant } from 'components/material-symbols/symbols';

import * as css from './styles.css';

interface InfoScreenProps {
	icon?: MaterialSymbolVariant;
	heading?: string;
	message?: string;
	children?: React.ReactNode;
}

export function InfoScreen({
	icon,
	heading,
	message,
	children,
}: InfoScreenProps): JSX.Element {
	const t = useT();
	const browserSupported = useIsBrowserSupported();

	return (
		<div className={css.wrapper}>
			<div className={css.backgroundImage}>
				<picture>
					<source
						media={`${styles.breakpoint.small} and (orientation: portrait)`}
						srcSet={`${getConfig().assetUrl}/img/StartImg-small.jpg`}
					/>
					<source
						media="(max-width:1920px)"
						srcSet={`${getConfig().assetUrl}/img/StartImg-medium.jpg`}
					/>
					<img src={`${getConfig().assetUrl}/img/StartImg-large.jpg`} />
				</picture>
				<div className={css.backgroundGradient} />
			</div>
			<div className={css.contentWrapper}>
				{icon && <MaterialSymbol variant={icon} className={css.icon} />}
				{heading && <h1 className={css.heading}>{heading}</h1>}
				{message && <p className={css.message}>{message}</p>}
				{browserSupported === false && (
					<InfoBox color="orange" className={css.outdated}>
						{isApp()
							? t(
									'You seem to be running an outdated OS on your device, please update to the latest version.'
								)
							: t(
									'You seem to be running an outdated browser, please update to the latest version.'
								)}
					</InfoBox>
				)}
				{children && <div className={css.children}>{children}</div>}
			</div>
			<Logo className={css.logo} />
		</div>
	);
}
